import React, { useEffect, useState } from 'react';
import { Star, Trash2 } from 'lucide-react';
import { reportFavoritesAPI } from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';

/**
 * Componente reutilizável para favoritar filtros de relatório.
 *
 * Props:
 *   modulo: 'projetos' | 'financeiro'
 *   filtrosAtuais: objeto plain com os filtros atuais (será persistido no favorito)
 *   onAplicarFavorito(filtros): callback chamado quando o usuário clica em um favorito para carregar
 */
export default function ReportFavorites({ modulo, filtrosAtuais, onAplicarFavorito }) {
  const [favoritos, setFavoritos] = useState([]);
  const [show, setShow] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [nomeNovo, setNomeNovo] = useState('');

  const load = async () => {
    try {
      const r = await reportFavoritesAPI.list(modulo);
      setFavoritos(r.data || []);
    } catch (e) {
      // silencioso
    }
  };

  useEffect(() => { load(); }, [modulo]);

  const salvar = async () => {
    const nome = (nomeNovo || '').trim();
    if (!nome) { toast.error('Informe um nome'); return; }
    try {
      await reportFavoritesAPI.create({ modulo, nome, filtros: filtrosAtuais || {} });
      toast.success('Favorito salvo');
      setNomeNovo('');
      setShowSaveDialog(false);
      load();
    } catch (e) {
      toast.error('Erro ao salvar favorito');
    }
  };

  const aplicar = (fav) => {
    onAplicarFavorito?.(fav.filtros || {});
    setShow(false);
    toast.success(`Filtros de "${fav.nome}" aplicados`);
  };

  const remover = async (fav, e) => {
    e.stopPropagation();
    if (!window.confirm(`Excluir favorito "${fav.nome}"?`)) return;
    try {
      await reportFavoritesAPI.delete(fav.id);
      toast.success('Excluído');
      load();
    } catch { toast.error('Erro'); }
  };

  return (
    <div className="relative inline-flex items-center gap-2" data-testid="report-favorites">
      <Button
        size="sm"
        variant="outline"
        onClick={() => setShow(v => !v)}
        data-testid="rel-fav-toggle"
        className="gap-1.5"
      >
        <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400" />
        Favoritos ({favoritos.length})
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setShowSaveDialog(true)}
        data-testid="rel-fav-save"
        title="Salvar filtros atuais como favorito"
      >
        + Salvar filtros
      </Button>

      {show && (
        <div
          className="absolute top-full left-0 mt-1 z-50 w-80 bg-white border rounded-lg shadow-xl max-h-96 overflow-y-auto"
          data-testid="rel-fav-list"
        >
          {favoritos.length === 0 ? (
            <p className="text-xs text-center text-muted-foreground py-6">Nenhum favorito salvo ainda.<br />Salve seus filtros atuais como modelo.</p>
          ) : (
            <ul className="divide-y">
              {favoritos.map(f => (
                <li key={f.id}>
                  <button
                    onClick={() => aplicar(f)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between gap-2"
                    data-testid={`rel-fav-${f.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{f.nome}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {Object.keys(f.filtros || {}).length} filtro(s) salvos
                      </p>
                    </div>
                    <button
                      onClick={(e) => remover(f, e)}
                      className="p-1 hover:bg-rose-50 rounded"
                      title="Excluir"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                    </button>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent data-testid="dialog-save-fav">
          <DialogHeader>
            <DialogTitle>Salvar modelo de relatório</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <p className="text-sm text-muted-foreground">
              Salva os filtros atuais para reutilizar rapidamente depois.
              Cada usuário tem seus próprios favoritos.
            </p>
            <Input
              placeholder="Ex: Serviços quitados de outubro"
              value={nomeNovo}
              onChange={(e) => setNomeNovo(e.target.value)}
              autoFocus
              data-testid="fav-nome-input"
              onKeyDown={(e) => { if (e.key === 'Enter') salvar(); }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>Cancelar</Button>
            <Button onClick={salvar} data-testid="btn-salvar-fav">Salvar favorito</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
